import React, { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import type { HomeFeedItem } from './homeTypes';
import apiService from '../../services/ApiService';
import CollectorCard from '../passport/CollectorCard';
import { defaultCardDesign } from '../../services/api/passportCards';
import {
  FEED_ACCENT_HEX,
  feedGlyphComponent,
  resolveFeedAccent,
  resolveFeedGlyph,
  splitFeedCountdown,
} from './feedIcons';

function categoryLabel(item: HomeFeedItem): string {
  if (item.status_label) return item.status_label;
  const cat = (item.category || item.variant || 'update').replace(/_/g, ' ');
  return cat.charAt(0).toUpperCase() + cat.slice(1);
}

function presentationOf(item: HomeFeedItem): string {
  return (item.presentation || 'compact').toLowerCase();
}

/** Accent border + tinted wash, matching Explore section surfaces (no left rail). */
function feedCardSurfaceStyle(accentHex: string, opts?: { strong?: boolean }): CSSProperties {
  const a = accentHex;
  return {
    borderColor: opts?.strong ? `${a}99` : `${a}55`,
    background: `linear-gradient(165deg, ${a}24 0%, ${a}0d 36%, rgba(14,14,14,0.94) 72%, rgba(10,10,10,0.96) 100%)`,
    boxShadow: `inset 0 1px 0 ${a}22`,
  };
}

function FeedGlyph({
  item,
  size = 'md',
}: {
  item: Pick<HomeFeedItem, 'variant' | 'category' | 'type' | 'action_required' | 'lane'>;
  size?: 'sm' | 'md' | 'lg';
}) {
  const accent = resolveFeedAccent(item);
  const glyph = resolveFeedGlyph(item);
  const Icon = feedGlyphComponent(glyph);
  const hex = FEED_ACCENT_HEX[accent];
  const box = size === 'lg' ? 'h-16 w-16' : size === 'sm' ? 'h-10 w-10' : 'h-[50px] w-[50px]';
  const icon = size === 'lg' ? 'h-8 w-8' : size === 'sm' ? 'h-[1.15rem] w-[1.15rem]' : 'h-6 w-6';
  const isCar = glyph === 'car';

  return (
    <div
      className={`relative flex ${box} shrink-0 items-center justify-center rounded-xl`}
      style={{
        color: hex,
        backgroundColor: `${hex}18`,
        boxShadow: `inset 0 0 0 1px ${hex}55, 0 0 20px -8px ${hex}66`,
      }}
      aria-hidden
      data-testid="feed-glyph"
      data-accent={accent}
      data-glyph={glyph}
    >
      <Icon className={`${icon}${isCar ? ' scale-[1.18]' : ''}`} strokeWidth={1.5} />
    </div>
  );
}

function ProgressBar({ progress }: { progress: { percent: number; label?: string } }) {
  return (
    <div className="mt-2.5" aria-label={`Progress ${progress.label || `${progress.percent}%`}`}>
      <div className="flex justify-between text-[11px] text-zinc-500 mb-1">
        <span>Progress</span>
        <span>{progress.label || `${Math.round(progress.percent)}%`}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full rounded-full bg-orange-500/80 transition-[width] duration-500 motion-reduce:transition-none"
          style={{ width: `${Math.min(100, Math.max(0, progress.percent))}%` }}
        />
      </div>
    </div>
  );
}

interface HomeFeedCardProps {
  item: HomeFeedItem;
  spanClass?: string;
}

export const HomeFeedCard: React.FC<HomeFeedCardProps> = ({ item, spanClass }) => {
  const navigate = useNavigate();
  const presentation = presentationOf(item);
  const accentKey = resolveFeedAccent(item);
  const accentHex = FEED_ACCENT_HEX[accentKey];
  const href = item.primary_action?.web_url || item.web_url || item.deep_link || null;
  const isUnread = item.read_state !== 'read' || item.action_required;
  const imageSrc = item.image_url ? apiService.getFileUrl(item.image_url) : null;
  const surface = feedCardSurfaceStyle(accentHex, { strong: Boolean(item.action_required) });

  const open = () => {
    void apiService.trackEvent('home_feed_item_opened', {
      metadata: {
        item_key: item.item_key,
        type: item.type,
        category: item.category,
        presentation,
      },
    });
    if (item.read_state !== 'read') {
      void apiService.setHomeFeedState('read', item.item_key).catch(() => undefined);
    }
    if (href) navigate(href);
  };

  const label = [
    categoryLabel(item),
    item.headline,
    item.time_label || '',
    item.action_required ? 'Action required' : '',
  ]
    .filter(Boolean)
    .join('. ');

  const shellClass = `group relative rounded-xl border transition-[border-color,background-color,transform,box-shadow] duration-200 motion-reduce:transition-none hover:brightness-[1.03] ${
    isUnread ? '' : 'opacity-[0.9]'
  } ${spanClass || ''}`;

  const actions = (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {href ? (
        <button
          type="button"
          onClick={open}
          className="min-h-[40px] cursor-pointer px-3 rounded-lg bg-white/[0.08] hover:bg-white/[0.14] text-sm text-white font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
        >
          {item.primary_action?.label || 'Open'}
        </button>
      ) : null}
      {item.secondary_action?.web_url ? (
        <button
          type="button"
          onClick={() => {
            void apiService.trackEvent('home_feed_action_completed', {
              metadata: { item_key: item.item_key, action: 'secondary' },
            });
            navigate(item.secondary_action!.web_url!);
          }}
          className="min-h-[40px] cursor-pointer px-3 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/[0.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
        >
          {item.secondary_action.label}
        </button>
      ) : null}
    </div>
  );

  const metaRow = (
    <div className="flex items-start justify-between gap-2">
      <p className="text-[11px] font-medium tracking-wide text-zinc-500">
        <span style={{ color: `${accentHex}cc` }}>{categoryLabel(item)}</span>
        {item.reason ? <span className="text-zinc-600"> · {item.reason}</span> : null}
        {item.time_label && presentation !== 'countdown' ? (
          <span className="text-zinc-600"> · {item.time_label}</span>
        ) : null}
      </p>
      {item.action_required ? (
        <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-300 bg-orange-500/10 border border-orange-500/25">
          Action
        </span>
      ) : isUnread ? (
        <span
          className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-400/90"
          aria-label="Unread"
        />
      ) : null}
    </div>
  );

  if (item.collector_subject) {
    const subject = item.collector_subject;
    return (
      <article className={`${shellClass} p-4`} style={surface} aria-label={label}>
        {metaRow}
        <p className="text-sm text-gray-300 my-3">{item.headline}</p>
        {item.body && <p className="text-sm text-gray-400 mb-3">{item.body}</p>}
        <div style={{ maxWidth: 360, minWidth: 280 }}>
          <CollectorCard
            card={{
              id: null,
              subject_type: subject.subject_type,
              subject_id: subject.subject_id,
              title: subject.title,
              issuer: 'Bilt:Spec',
              status: 'preview',
              design: defaultCardDesign,
              rules: { evidence: 'qr', cap: null },
              subject,
            }}
          />
        </div>
        {item.progress && <ProgressBar progress={item.progress} />}
        {actions}
      </article>
    );
  }

  if ((presentation === 'hero' || presentation === 'photo') && imageSrc) {
    return (
      <article
        className={`${shellClass} overflow-hidden`}
        style={surface}
        aria-label={label}
        data-presentation={presentation}
      >
        <button
          type="button"
          onClick={open}
          className="block w-full cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
        >
          <div
            className={`relative ${presentation === 'hero' ? 'aspect-[16/9] sm:aspect-[21/9]' : 'aspect-[4/3]'} bg-zinc-900`}
          >
            <img
              src={imageSrc as string}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div
              className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"
              aria-hidden
            />
            <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
              {metaRow}
              <h3 className="mt-1 font-display text-xl sm:text-2xl font-semibold text-white leading-tight text-balance">
                {item.headline}
              </h3>
              {item.body ? (
                <p className="mt-1.5 text-sm text-zinc-300 line-clamp-2 max-w-xl">{item.body}</p>
              ) : null}
            </div>
          </div>
        </button>
        <div className="px-4 pb-3 sm:px-5">{actions}</div>
      </article>
    );
  }

  if (presentation === 'stat' || presentation === 'countdown') {
    const raw =
      item.progress?.label ||
      item.time_label ||
      (item.progress ? `${Math.round(item.progress.percent)}%` : null) ||
      categoryLabel(item);
    const split = splitFeedCountdown(raw);
    const subLabel =
      item.progress?.label && item.time_label && item.time_label !== item.progress.label
        ? item.time_label
        : null;
    return (
      <article
        className={`${shellClass} px-4 py-4`}
        style={surface}
        aria-label={label}
        data-presentation={presentation}
      >
        <div className="flex gap-3.5">
          <FeedGlyph item={item} size="md" />
          <div className="min-w-0 flex-1">
            {metaRow}
            <div className="mt-2 flex items-baseline gap-2">
              <p className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-none text-white">
                {split.primary}
              </p>
              {split.secondary ? (
                <span className="text-sm font-medium text-zinc-400">{split.secondary}</span>
              ) : null}
            </div>
            {subLabel ? <p className="mt-1 text-xs text-zinc-500">{subLabel}</p> : null}
            <h3 className="mt-2.5 font-display text-base font-medium text-white leading-snug">
              {item.headline}
            </h3>
            {item.body ? (
              <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{item.body}</p>
            ) : null}
            {item.progress && item.progress.percent > 0 ? (
              <ProgressBar progress={item.progress} />
            ) : null}
            {actions}
          </div>
        </div>
      </article>
    );
  }

  if (presentation === 'progress') {
    return (
      <article
        className={`${shellClass} px-4 py-4`}
        style={surface}
        aria-label={label}
        data-presentation="progress"
      >
        <div className="flex gap-3">
          <FeedGlyph item={item} size="md" />
          <div className="min-w-0 flex-1">
            {metaRow}
            <h3 className="mt-2 font-display text-lg font-medium text-white">{item.headline}</h3>
            {item.body ? <p className="mt-1 text-sm text-zinc-400">{item.body}</p> : null}
            {item.progress ? <ProgressBar progress={item.progress} /> : null}
            {actions}
          </div>
        </div>
      </article>
    );
  }

  if (presentation === 'wide' || presentation === 'medium') {
    return (
      <article
        className={`${shellClass} overflow-hidden`}
        style={surface}
        aria-label={label}
        data-presentation={presentation}
      >
        <div
          className={`flex ${presentation === 'wide' && imageSrc ? 'flex-col sm:flex-row' : 'gap-3.5'} p-3.5 sm:p-4`}
        >
          {imageSrc ? (
            <img
              src={imageSrc as string}
              alt=""
              loading="lazy"
              className={
                presentation === 'wide'
                  ? 'w-full sm:w-44 h-36 sm:h-auto object-cover rounded-lg bg-zinc-900 shrink-0'
                  : 'h-16 w-16 shrink-0 rounded-xl object-cover bg-zinc-900 ring-1 ring-white/10'
              }
            />
          ) : (
            <FeedGlyph item={item} size={presentation === 'wide' ? 'lg' : 'md'} />
          )}
          <div className="min-w-0 flex-1 mt-3 sm:mt-0">
            {metaRow}
            <h3 className="mt-1 font-display text-base sm:text-lg font-medium text-white leading-snug text-balance">
              {item.headline}
            </h3>
            {item.body ? (
              <p className="mt-1 text-sm text-zinc-400 leading-relaxed line-clamp-3">{item.body}</p>
            ) : null}
            {item.progress ? <ProgressBar progress={item.progress} /> : null}
            {actions}
          </div>
        </div>
      </article>
    );
  }

  if (presentation === 'square' && imageSrc) {
    return (
      <article
        className={`${shellClass} overflow-hidden`}
        style={surface}
        aria-label={label}
        data-presentation="square"
      >
        <button
          type="button"
          onClick={open}
          className="block w-full cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
        >
          <div className="aspect-square relative bg-zinc-900">
            <img
              src={imageSrc as string}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div
              className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent"
              aria-hidden
            />
            <div className="absolute inset-x-0 bottom-0 p-3">
              <p
                className="text-[10px] font-medium uppercase tracking-wide"
                style={{ color: `${accentHex}cc` }}
              >
                {categoryLabel(item)}
              </p>
              <h3 className="font-display text-sm font-medium text-white line-clamp-2 mt-0.5">
                {item.headline}
              </h3>
            </div>
          </div>
        </button>
      </article>
    );
  }

  return (
    <article
      className={`${shellClass} px-3.5 py-3.5 sm:px-4`}
      style={surface}
      aria-label={label}
      data-presentation={presentation === 'inline' ? 'inline' : 'compact'}
    >
      <div className="flex gap-3.5">
        {imageSrc ? (
          <img
            src={imageSrc as string}
            alt=""
            loading="lazy"
            className="h-[50px] w-[50px] shrink-0 rounded-xl object-cover bg-zinc-900 ring-1 ring-white/10"
          />
        ) : (
          <FeedGlyph item={item} />
        )}
        <div className="min-w-0 flex-1">
          {metaRow}
          <h3 className="mt-1 font-display text-[15px] sm:text-base font-medium text-white leading-snug text-balance">
            {item.headline}
          </h3>
          {item.body ? (
            <p className="mt-1 text-sm text-zinc-400 leading-relaxed line-clamp-2">{item.body}</p>
          ) : null}
          {item.progress ? <ProgressBar progress={item.progress} /> : null}
          {actions}
        </div>
      </div>
    </article>
  );
};

/** @deprecated Uniform full-width cards; kept for callers that still import it. */
export function feedItemSpanClass(_presentation?: string | null): string {
  return 'min-w-0';
}

export default HomeFeedCard;
